import { useState, useEffect, useRef, useTransition } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Calculator, Menu, ChevronRight, ChevronDown, Search, Bell, Plus,
  UserPlus, FilePlus, Building2, CreditCard, Sun, Moon, Monitor,
  LogOut, User, Settings, HelpCircle, FileText, CheckCircle2
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { cn } from './shared/lib/utils';
import { MENU_ITEMS } from './shared/constants';
import { useTheme } from './shared/hooks/useTheme';
import { useAuth } from './shared/context/AuthContext';
import { useFeatureToggle } from './shared/context/FeatureToggleContext';
import { useKeyboardShortcuts } from './shared/hooks/useKeyboardShortcuts';
import Sidebar from './shared/ui/Sidebar';
import LoginView from './features/auth/components/LoginView';
import AppRouter from './app/Router';
import MobileBottomNav from './shared/ui/MobileBottomNav';
import OfflineIndicator from './shared/ui/OfflineIndicator';
import InstallPrompt from './shared/ui/InstallPrompt';
import SyncIndicator from './shared/ui/SyncIndicator';
import CommandPalette from './shared/ui/CommandPalette';
import { useBackGesture } from './shared/hooks/useBackGesture';
import { initSyncEngine } from './shared/lib/syncEngine';

export default function App() {
  const { theme, setTheme } = useTheme();
  const { user, isAuthenticated, login, logout } = useAuth();
  const { isEnabled } = useFeatureToggle();
  const backGestureHandlers = useBackGesture();

  // Initialize offline sync engine
  useEffect(() => { initSyncEngine(); }, []);

  const [, startTransition] = useTransition();

  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarCollapsed') || 'false'); }
    catch { return false; }
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.hash || '#');
  const [isDashboardView, setIsDashboardView] = useState(() => {
    const hash = window.location.hash;
    return isAuthenticated && !!hash && hash !== '#' && hash !== '#features' && hash !== '#ai-assistant' && hash !== '#pricing' && hash !== '#about';
  });

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'New Invoice Received', desc: 'Invoice #INV-2024-001 from Wayne Enterprises', time: '5 min ago', icon: FileText, color: 'text-blue-500 bg-blue-500/10', read: false },
    { id: 2, title: 'Payment Successful', desc: 'Payment of $1,250.00 received from Stark Ind.', time: '1 hour ago', icon: CheckCircle2, color: 'text-emerald-500 bg-emerald-500/10', read: false },
    { id: 3, title: 'System Update', desc: 'System maintenance scheduled for tonight', time: '2 hours ago', icon: Settings, color: 'text-amber-500 bg-amber-500/10', read: false },
  ]);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { key: 'k', ctrl: true, description: 'Open command palette', action: () => setCommandPaletteOpen(true) },
    { key: 'Escape', description: 'Close command palette', action: () => setCommandPaletteOpen(false) },
    { key: '1', ctrl: true, description: 'Dashboard', action: () => { window.location.hash = '#dashboard'; } },
    { key: '2', ctrl: true, description: 'Customers', action: () => { window.location.hash = '#ar-customers'; } },
    { key: '3', ctrl: true, description: 'Invoices', action: () => { window.location.hash = '#invoicing'; } },
    { key: ',', ctrl: true, description: 'Settings', action: () => { window.location.hash = '#settings-main'; } },
  ], isAuthenticated);
  const unreadCount = notifications.filter(n => !n.read).length;

  const handleGoToIntro = () => {
    sessionStorage.removeItem('majelani_intro_seen');
    document.body.style.transition = 'opacity 0.5s ease';
    document.body.style.opacity = '0';
    setTimeout(() => { window.location.href = 'https://majelaniaccounting.com'; }, 500);
  };

  const handleLogout = () => {
    logout();
    setIsDashboardView(false);
  };

  const handleGetStarted = () => {
    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
    } else {
      window.location.hash = '#dashboard';
      setIsDashboardView(true);
    }
  };

  const handleLogin = (userData: any, token?: string) => {
    login(userData, token);
    window.location.hash = '#dashboard';
    setIsLoginModalOpen(false);
    setCurrentPath('#dashboard');
    setIsDashboardView(true);
  };

  useEffect(() => {
    const handleHashChange = () => {
      const fullHash = window.location.hash;
      const hash = fullHash.split('?')[0];
      const isLandingRoute = !hash || hash === '#' || hash === '#features' || hash === '#ai-assistant' || hash === '#pricing' || hash === '#about';

      startTransition(() => {
        if (!isAuthenticated) {
          setIsDashboardView(false);
          if (!isLandingRoute) {
            window.history.replaceState(null, '', '#');
            setCurrentPath('#');
          } else {
            setCurrentPath(hash || '#');
          }
        } else {
          if (isLandingRoute) {
            window.location.hash = '#dashboard';
            return;
          }
          setIsDashboardView(true);
          setCurrentPath(hash);
        }
      });
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    let retryCount = 0;
    const checkHealth = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        await fetch('/api/health', { signal: controller.signal });
        clearTimeout(timeoutId);
      } catch {
        if (retryCount++ < 5) setTimeout(checkHealth, 5000);
      }
    };
    checkHealth();
  }, []);

  const activeMenuSection = MENU_ITEMS.find(item =>
    item.href === currentPath || item.subItems?.some(sub => sub.href === currentPath)
  );

  // Filter out toggled-off subItems from header dropdown
  const TOGGLE_SUB_ITEMS: Record<string, string> = { '#plan-billing': 'billing', '#pricing': 'pricing' };
  const filteredSubItems = activeMenuSection?.subItems?.filter(sub => {
    const toggleKey = TOGGLE_SUB_ITEMS[sub.href || ''];
    return !toggleKey || isEnabled(toggleKey);
  });

  return (
    <div className="h-screen w-screen overflow-hidden flex bg-surface dark:bg-[#020617] transition-colors duration-300 relative">
      <Toaster position="top-center" richColors />
      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} onNavigate={(hash) => { window.location.hash = hash; setCommandPaletteOpen(false); }} />

      <AnimatePresence>
        {isLoginModalOpen && !isAuthenticated && (
          <LoginView defaultIsLogin={true} onLogin={handleLogin} onBack={() => setIsLoginModalOpen(false)} />
        )}
      </AnimatePresence>

      {isDashboardView && (
        <aside className={cn(
          "fixed inset-y-0 left-0 z-50 hidden lg:block sidebar-transition",
          isSidebarCollapsed ? "w-[80px]" : "w-[260px]"
        )}>
          <Sidebar
            isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed}
            isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            currentPath={currentPath} setIsDashboardView={setIsDashboardView} user={user}
          />
        </aside>
      )}

      <AnimatePresence>
        {isDashboardView && isMobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] lg:hidden" />
            <motion.aside initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-[280px] z-[70] lg:hidden">
              <Sidebar
                isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed}
                isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen}
                searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                currentPath={currentPath} setIsDashboardView={setIsDashboardView} user={user}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className={cn(
        "flex-1 flex flex-col min-h-0 min-w-0 sidebar-transition",
        isDashboardView && (isSidebarCollapsed ? "lg:ml-[80px]" : "lg:ml-[260px]")
      )}>
        <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-colors duration-300 safe-top">
          <div className="px-3 sm:px-5 lg:px-6">
            <div className="flex justify-between h-12 items-center">
              <div className="flex items-center gap-4">
                <button onClick={() => setIsMobileMenuOpen(true)}
                  className={cn("p-2 text-slate-600 dark:text-slate-400 lg:hidden hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg", !isDashboardView && "hidden")}>
                  <Menu className="w-5 h-5" />
                </button>

                {!isDashboardView ? (
                  <div className="flex items-center gap-2">
                    <button onClick={handleGoToIntro} className="flex items-center gap-2 hover:opacity-80 transition-opacity" title="Back to Intro">
                      <Calculator className="w-6 h-6 text-primary" />
                      <span className="text-xl font-display font-bold text-slate-900 dark:text-white">Majelani</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="hidden sm:flex items-center gap-2 text-slate-500 dark:text-slate-500 text-sm">
                      <span onClick={() => { window.location.hash = '#'; setIsDashboardView(false); }}
                        className="cursor-pointer hover:text-primary transition-colors">Majelani</span>
                      <ChevronRight className="w-4 h-4" />
                      <span onClick={() => window.location.hash = '#dashboard'}
                        className={cn("cursor-pointer hover:text-primary transition-colors",
                          currentPath === '#dashboard' ? "text-slate-900 dark:text-white font-medium" : "")}>
                        Dashboard
                      </span>
                    </div>

                    {activeMenuSection && activeMenuSection.id !== 'dashboard' && filteredSubItems && filteredSubItems.length > 0 && (
                      <div className="relative">
                        <button onClick={() => setIsHeaderDropdownOpen(!isHeaderDropdownOpen)}
                          className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-blue-50 dark:bg-[#112240] border border-blue-200 dark:border-blue-500/30 rounded-xl text-blue-600 dark:text-blue-500 text-xs sm:text-sm font-medium hover:bg-blue-100 dark:hover:bg-[#1a2f55] transition-colors shadow-sm min-w-0">
                          <activeMenuSection.icon className="w-4 h-4 text-blue-600 dark:text-blue-500 shrink-0" />
                          <span className="truncate">{activeMenuSection.label}</span>
                          <ChevronDown className={cn("w-4 h-4 transition-transform", isHeaderDropdownOpen && "rotate-180")} />
                        </button>
                        <AnimatePresence>
                          {isHeaderDropdownOpen && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setIsHeaderDropdownOpen(false)} />
                              <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="absolute top-full left-0 mt-2 w-[calc(100vw-2rem)] sm:w-64 bg-white dark:bg-[#0a1428] border border-slate-200 dark:border-blue-500/20 rounded-xl shadow-xl overflow-hidden z-20 p-2">
                                <div className="space-y-0.5 max-h-[50vh] overflow-y-auto custom-scrollbar">
                                  {filteredSubItems.map((item, idx) => (
                                    item.separator ? (
                                      <div key={`sep-${idx}`} className="px-4 pt-3 pb-1 first:pt-1">
                                        <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">{item.label}</p>
                                        <div className="h-px bg-slate-200 dark:bg-slate-700/50 mt-1" />
                                      </div>
                                    ) : (
                                    <a key={item.href} href={item.href} onClick={() => setIsHeaderDropdownOpen(false)}
                                      className={cn("flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all relative group",
                                        currentPath === item.href ? "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-500"
                                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-200")}>
                                      {currentPath === item.href && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-500 rounded-r-full" />}
                                      <span className={currentPath === item.href ? "ml-2" : ""}>{item.label}</span>
                                    </a>
                                    )
                                  ))}
                                </div>
                              </motion.div>
                            </>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                {/* Intro Button */}
                <button onClick={handleGoToIntro} title="Back to Intro"
                  className="p-2 text-slate-400 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
                  style={{ fontSize: 0 }}>
                  <Calculator className="w-4 h-4" />
                </button>

                {/* Theme Toggle */}
                <div className="relative">
                  <button onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                    className="p-2.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all relative group">
                    {theme === 'light' && <Sun className="w-5 h-5" />}
                    {theme === 'dark' && <Moon className="w-5 h-5" />}
                    {theme === 'system' && <Monitor className="w-5 h-5" />}
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <AnimatePresence>
                    {isThemeMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setIsThemeMenuOpen(false)} />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }}
                          className="absolute right-0 mt-2 w-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-20 overflow-hidden p-1.5">
                          {[
                            { id: 'light', label: 'Light', icon: Sun },
                            { id: 'dark', label: 'Dark', icon: Moon },
                            { id: 'system', label: 'System', icon: Monitor }
                          ].map((item) => (
                            <button key={item.id} onClick={() => { setTheme(item.id as any); setIsThemeMenuOpen(false); }}
                              className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all",
                                theme === item.id ? "bg-primary/10 text-primary" : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800")}>
                              <item.icon className="w-4 h-4" />
                              {item.label}
                            </button>
                          ))}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile-only: compact action buttons */}
                {isDashboardView && (
                  <div className="flex md:hidden items-center gap-1">
                    <div className="relative">
                      <button onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                        className="relative p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all">
                        <Bell className="w-4 h-4" />
                        {unreadCount > 0 && (
                          <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-rose-500 text-white text-[8px] font-bold flex items-center justify-center rounded-full">
                            {unreadCount}
                          </span>
                        )}
                      </button>
                    </div>
                    <div className="relative">
                      <button onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all">
                        <User className="w-4 h-4" />
                      </button>
                      {/* Mobile Profile Dropdown */}
                      <AnimatePresence>
                        {isProfileOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setIsProfileOpen(false)} />
                            <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              className="absolute top-full right-0 mt-2 w-[calc(100vw-2rem)] sm:w-64 bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-20 overflow-hidden">
                              <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-white shrink-0">
                                    {user?.name ? user.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                  </div>
                                  <div className="overflow-hidden">
                                    <p className="text-sm font-bold truncate text-slate-900 dark:text-white">{user?.name || 'User'}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email || ''}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="p-1.5">
                                <button onClick={() => { window.location.hash = '#settings-main'; setIsProfileOpen(false); }}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 text-left">
                                  <Settings className="w-4 h-4" /> Settings
                                </button>
                                <button onClick={() => { window.location.hash = '#help-center'; setIsProfileOpen(false); }}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 text-left">
                                  <HelpCircle className="w-4 h-4" /> Help & Support
                                </button>
                                <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
                                <button onClick={() => { handleLogout(); setIsProfileOpen(false); }}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left">
                                  <LogOut className="w-4 h-4" /> Log Out
                                </button>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {isDashboardView && (
                  <div className="hidden md:flex items-center gap-4 mr-4">
                    <div className="relative group">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-cyan-400 transition-colors" />
                      <input type="text" placeholder="Search customers..."
                        className="bg-slate-100 dark:bg-slate-800 border-transparent rounded-full py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:bg-white dark:focus:bg-slate-700 transition-all w-64 text-slate-900 dark:text-white" />
                    </div>

                    {/* Notifications */}
                    <div className="relative">
                      <button onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                        className="relative p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-all">
                        <Bell className="w-5 h-5" />
                        {unreadCount > 0 && (
                          <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white dark:border-slate-900">
                            {unreadCount}
                          </span>
                        )}
                      </button>
                      <AnimatePresence>
                        {isNotificationOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setIsNotificationOpen(false)} />
                            <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              className="absolute top-full right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-20 overflow-hidden">
                              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                                <h3 className="font-bold text-slate-900 dark:text-white text-sm">Notifications</h3>
                                <button onClick={() => setNotifications(n => n.map(x => ({ ...x, read: true })))}
                                  className="text-xs text-primary font-medium hover:underline disabled:opacity-50" disabled={unreadCount === 0}>
                                  Mark all as read
                                </button>
                              </div>
                              <div className="max-h-[300px] overflow-y-auto">
                                {notifications.map((notif) => (
                                  <div key={notif.id} className={cn("px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer border-b border-slate-100 dark:border-slate-800/50 last:border-0",
                                    notif.read ? "opacity-60" : "bg-slate-50/50 dark:bg-slate-800/20")}>
                                    <div className="flex gap-3">
                                      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", notif.color)}>
                                        <notif.icon className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-200">{notif.title}</h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{notif.desc}</p>
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{notif.time}</p>
                                      </div>
                                      {!notif.read && <div className="w-2 h-2 rounded-full bg-primary mt-1.5 ml-auto flex-shrink-0" />}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="p-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                                <button onClick={() => { toast.info('Navigating to all notifications view...'); setIsNotificationOpen(false); }}
                                  className="w-full py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-primary transition-colors">
                                  View all notifications
                                </button>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Quick Add */}
                    <div className="relative">
                      <button onClick={() => setIsQuickAddOpen(!isQuickAddOpen)}
                        className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20">
                        <Plus className="w-4 h-4" /> Quick Add
                      </button>
                      <AnimatePresence>
                        {isQuickAddOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setIsQuickAddOpen(false)} />
                            <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-20 overflow-hidden p-1.5">
                              {[
                                { label: 'New Customer', icon: UserPlus, action: () => toast.info('New Customer Modal') },
                                { label: 'New Invoice', icon: FilePlus, action: () => toast.info('New Invoice Modal') },
                                { label: 'New Vendor', icon: Building2, action: () => toast.info('New Vendor Modal') },
                                { label: 'Record Payment', icon: CreditCard, action: () => toast.info('Record Payment Modal') }
                              ].map((item, idx) => (
                                <button key={idx} onClick={() => { item.action(); setIsQuickAddOpen(false); }}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all text-left">
                                  <item.icon className="w-4 h-4" /> {item.label}
                                </button>
                              ))}
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Profile */}
                    <div className="relative">
                      <button onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
                        <User className="w-4 h-4" /> Profile
                      </button>
                      <AnimatePresence>
                        {isProfileOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setIsProfileOpen(false)} />
                            <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              className="absolute top-full right-0 mt-2 w-[calc(100vw-2rem)] sm:w-64 bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-20 overflow-hidden">
                              <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-white shrink-0">
                                    {user?.name ? user.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                  </div>
                                  <div className="overflow-hidden">
                                    <p className="text-sm font-bold truncate text-slate-900 dark:text-white">{user?.name || 'User'}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email || 'user@example.com'}</p>
                                    {user?.role && (
                                      <p className="text-[10px] font-bold text-primary mt-0.5">{user.role.label}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="p-1.5">
                                {[
                                  { label: 'My Profile', icon: User, action: () => { window.location.hash = '#user-mgmt'; } },
                                  { label: 'Settings', icon: Settings, action: () => { window.location.hash = '#settings-main'; } },
                                  { label: 'Help & Support', icon: HelpCircle, action: () => { window.location.hash = '#help-center'; } },
                                ].map((item, idx) => (
                                  <button key={idx} onClick={() => { item.action(); setIsProfileOpen(false); }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all text-left">
                                    <item.icon className="w-4 h-4" /> {item.label}
                                  </button>
                                ))}
                                <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
                                <button onClick={() => { handleLogout(); setIsProfileOpen(false); }}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all text-left">
                                  <LogOut className="w-4 h-4" /> Log Out
                                </button>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {!isDashboardView && (
                  <>
                    <div className="hidden sm:flex items-center gap-6 mr-4">
                      <a href="#features" className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary transition-colors">Features</a>
                      {isEnabled('pricing') && <a href="#pricing" className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary transition-colors">Pricing</a>}
                      <a href="#ai-assistant" className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary transition-colors">AI Assistant</a>
                    </div>
                    <button onClick={handleGetStarted}
                      className="bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-sm">
                      {isAuthenticated ? 'Go to Dashboard' : 'Get Started'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 flex flex-col min-h-0 min-w-0 pb-14 sm:pb-0" {...backGestureHandlers}>
          <AppRouter currentPath={currentPath} isDashboardView={isDashboardView} user={user} onGetStarted={handleGetStarted} />
        </main>
        {isDashboardView && <MobileBottomNav />}
        <OfflineIndicator />
        <InstallPrompt />
        <SyncIndicator />
      </div>
    </div>
  );
}
